// Dashboard.js — Home (redesign 2026-10, mockup "Home B2B goooled").
// Saluto + azioni, riprendi il carrello, ultimi ordini, schede per configurare e ordinare.

import React, { useContext, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiClipboard, FiPlus, FiArrowRight } from 'react-icons/fi';
import '../css/Home.css';
import { urlAsset } from './utils';
import { useLanguage } from '../context/LanguageContext';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import { OrderContext } from '../context/OrderContext';
import { useItemImage } from './cartImages';

const LAST_ORDERS = 3;

const formatEuro = (value) =>
  `€ ${Number(value || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Stato ordine → colore della barra e della pastiglia
const STATUS = {
  preparato: { cls: 'prep', key: 'home.statusPrepared' },
  rifiutato: { cls: 'rej', key: 'home.statusRejected' },
  inviato: { cls: 'sent', key: 'home.statusSent' },
  ricevuto: { cls: 'ok', key: 'home.statusReceived' },
};

const Dashboard = ({ showPrices = true }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const { cart = [], currentOrder } = useContext(CartContext) || {};
  const { orders = [] } = useContext(OrderContext) || {};
  const imageOf = useItemImage();
  const SHOW_ON_SALE = false;
  const SHOW_NEW_ARRIVALS = true;

  const cards = useMemo(() => ([
    {
      id: 'custom',
      to: '/configurator',
      image: '/images/profile_custom.jpg',
      title: t('home.customProfiles'),
      description: t('home.cardCustomDescription'),
      eta: t('home.cardCustomEta'),
      category: t('home.categoryConfigurator'),
      priority: 'primary',
    },
    {
      id: 'loose',
      to: '/looseprofiles',
      image: '/images/aluminum_profiles.jpg',
      title: t('home.emptyProfiles'),
      description: t('home.cardLooseDescription'),
      eta: t('home.cardLooseEta'),
      category: t('home.categoryProfiles'),
    },
    {
      id: 'strip',
      to: '/stripled',
      image: '/images/strip_led.jpg',
      title: t('home.stripLed'),
      description: t('home.cardStripDescription'),
      eta: t('home.cardStripEta'),
      category: t('home.categoryLighting'),
    },
    {
      id: 'power',
      to: '/alimentatori',
      image: '/images/alimentatori.jpg',
      title: t('home.powerSupplies'),
      description: t('home.cardPowerDescription'),
      eta: t('home.cardPowerEta'),
      category: t('home.categoryElectronics'),
    },
    {
      id: 'catalog',
      to: '/catalogo',
      image: '/images/catalog.jpg',
      title: t('home.catalog'),
      description: t('home.cardCatalogDescription'),
      eta: t('home.cardCatalogEta'),
      category: t('home.categoryCatalog'),
    },
    {
      id: 'btm',
      to: '/btmconfigurator',
      image: '/images/btm_system.jpg',
      title: t('home.btmSystem'),
      description: t('home.cardBtmDescription'),
      eta: t('home.cardBtmEta'),
      category: t('home.categorySystems'),
    },
    ...(SHOW_NEW_ARRIVALS
      ? [{
        id: 'newArrivals',
        to: '/novita',
        image: '/images/novità.png',
        title: t('home.newArrivalsTitle'),
        description: t('home.newArrivalsSubtitle'),
        eta: t('home.cardNewArrivalsEta'),
        category: t('home.categoryNews'),
      }]
      : []),
    ...(SHOW_ON_SALE
      ? [{
        id: 'sale',
        to: '/onsale',
        image: '/images/offerta1.webp',
        title: t('home.onSaleTitle'),
        description: t('home.onSaleSubtitle'),
        eta: t('home.cardOnSaleEta'),
        category: t('home.categoryPromo'),
      }]
      : []),
  ]), [SHOW_NEW_ARRIVALS, SHOW_ON_SALE, t]);

  // Saluto in base all'ora
  const hour = new Date().getHours();
  const greeting = hour < 13 ? t('home.greetMorning') : hour < 18 ? t('home.greetAfternoon') : t('home.greetEvening');
  const firstName = String(user?.name || '').trim().split(/\s+/)[0];
  const canQuote = user?.role === 'admin' || !!user?.quoteEnabled;

  // Carrello: totale netto come nel carrello (sconto riga, altrimenti sconto profilo)
  const rowDiscount = (item) => {
    if (item.type === 'adjustment') return 0;
    if (item.discount !== null && item.discount !== undefined && item.discount !== '' && !isNaN(Number(item.discount))) {
      return Number(item.discount);
    }
    return Number(user?.discount || 0);
  };
  const cartTotal = cart.reduce(
    (sum, item) => sum + Number(item.unitPrice || 0) * (1 - rowDiscount(item) / 100) * Number(item.quantity || 0),
    0
  );
  const productLines = cart.filter((item) => item.type !== 'adjustment');
  const zoneCount = new Set(cart.map((item) => String(item.zone || '').trim()).filter(Boolean)).size;
  const thumbs = productLines.map((item) => ({ item, src: imageOf(item) })).filter((x) => x.src).slice(0, 4);
  const cartTitle = currentOrder
    ? `${t('home.editingOrder')} #${currentOrder.orderNumber || ''}${currentOrder.reference ? ` · ${currentOrder.reference}` : ''}`
    : canQuote ? t('home.draftQuote') : t('home.draftOrder');

  // Stessa regola della pagina Ordini: solo i propri ordini e quelli condivisi dalla propria azienda
  // (l'admin riceve dal server TUTTI gli ordini: senza questo filtro vedrebbe quelli dei clienti).
  const lastOrders = useMemo(
    () => orders
      .filter((order) =>
        String(order.userId) === String(user?._id) ||
        (order.companyKey &&
          user?.companyKey &&
          order.companyKey === user.companyKey &&
          order.companySharingEnabled !== false &&
          user.companySharingEnabled !== false))
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
      .slice(0, LAST_ORDERS),
    [orders, user]
  );

  return (
    <div className="home2">
      <section className="home2-hello">
        <div>
          <h1>{firstName ? `${greeting}, ${firstName}` : greeting}</h1>
          <p>{[user?.companyName, t('home.todayQuestion')].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="home2-cta">
          <button type="button" className="home2-btn" onClick={() => navigate('/orders')}>
            <FiClipboard aria-hidden="true" />
            {t('sidebar.orders')}
          </button>
          <button type="button" className="home2-btn is-primary" onClick={() => navigate('/configurator')}>
            <FiPlus aria-hidden="true" />
            {canQuote ? t('home.newQuote') : t('home.newOrder')}
          </button>
        </div>
      </section>

      <section className="home2-work">
        <div className="home2-card">
          <h2>
            {t('home.resumeCart')}
            <Link to="/cart">{t('home.openCart')} →</Link>
          </h2>
          {cart.length === 0 ? (
            <p className="home2-empty">{t('home.cartEmpty')}</p>
          ) : (
            <Link to="/cart" className="home2-resume">
              <span className="home2-thumbs" aria-hidden="true">
                {thumbs.map(({ item, src }, i) => (
                  <img
                    key={item.id ?? i}
                    src={src}
                    alt=""
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ))}
              </span>
              <span className="home2-resume-text">
                <strong>{cartTitle}</strong>
                <span>
                  {t('home.linesCount', { count: productLines.length })}
                  {zoneCount > 0 ? ` · ${t('home.zonesCount', { count: zoneCount })}` : ''}
                </span>
              </span>
              {showPrices && <span className="home2-total">{formatEuro(cartTotal)}</span>}
            </Link>
          )}
        </div>

        <div className="home2-card">
          <h2>
            {t('home.lastOrders')}
            <Link to="/orders">{t('home.seeAll')} →</Link>
          </h2>
          {lastOrders.length === 0 ? (
            <p className="home2-empty">{t('home.noOrders')}</p>
          ) : (
            <div className="home2-orders">
              {lastOrders.map((order) => {
                const st = STATUS[order.status] || STATUS.preparato;
                const who = order.recipient?.company || order.recipient?.name;
                return (
                  <Link key={order._id} to="/orders" className={`home2-order st-${st.cls}`}>
                    <span className="home2-order-n">#{order.orderNumber}</span>
                    <span className="home2-order-c">{[who, order.reference].filter(Boolean).join(' · ') || '—'}</span>
                    {showPrices && <span className="home2-order-amt">{formatEuro(order.total)}</span>}
                    <span className={`home2-pill ${st.cls}`}>{t(st.key)}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <div className="home2-sect">
        <h2>{t('home.sectionTitle')}</h2>
        <span>{t('home.sectionHint')}</span>
      </div>

      <section className="home2-tiles">
        {cards.map((card) => (
          <Link key={card.id} to={card.to} className={`home2-tile${card.priority === 'primary' ? ' is-primary' : ''}`}>
            <span className="home2-ph">
              <img src={urlAsset(card.image)} alt="" loading="lazy" />
              <span className="home2-cat">{card.category}</span>
            </span>
            <span className="home2-tx">
              <strong>{card.title}</strong>
              <span className="home2-desc">{card.description}</span>
              <span className="home2-meta">
                <span>{card.eta}</span>
                <span className="home2-go">{t('home.open')} <FiArrowRight aria-hidden="true" /></span>
              </span>
            </span>
          </Link>
        ))}
      </section>
    </div>
  );
};

export default Dashboard;
